import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import LessonTestBuilder from './LessonTestBuilder';
import LessonTestEditor from './LessonTestEditor';
import '../tests/TestManager.css'; // Reusing existing styles

const LessonTestManager = () => {
  const [lessonTests, setLessonTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLessonTests();
  }, []);

  const loadLessonTests = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'lessonTests'), orderBy('createdAt', 'desc')); // Newest first
      const querySnapshot = await getDocs(q);
      const testsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLessonTests(testsData);
    } catch (error) {
      console.error('Error loading lesson tests:', error);
      alert('Failed to load lesson tests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = () => {
    setShowBuilder(true);
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this lesson test?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'lessonTests', testId));
      setLessonTests(lessonTests.filter(t => t.id !== testId));
      alert('Lesson test deleted successfully');
    } catch (error) {
      console.error('Error deleting lesson test:', error);
      alert('Failed to delete lesson test');
    }
  };

  const handleBuilderClose = () => {
    setShowBuilder(false);
    loadLessonTests();
  };

  const handleEditorClose = () => {
    setEditingTest(null);
    loadLessonTests();
  };

  // Group tests by level
  const testsByLevel = lessonTests.reduce((acc, test) => {
    if (!acc[test.levelId]) {
      acc[test.levelId] = [];
    }
    acc[test.levelId].push(test);
    return acc;
  }, {});

  // Filter tests by search query
  const filterTests = (tests) => {
    if (!searchQuery.trim()) return tests;
    const query = searchQuery.toLowerCase();
    return tests.filter(test => 
      test.lessonTitle.toLowerCase().includes(query)
    );
  };

  const levelNames = {
    'level-1': 'Level 1 - Roots',
    'level-2': 'Level 2 - Branches',
    'level-3': 'Level 3 - Skies',
    'level-4': 'Level 4 - Rivers'
  };

  if (loading) {
    return <div className="loading">Loading lesson tests...</div>;
  }

  return (
    <div className="lesson-test-manager">
      <div className="manager-header">
        <h2>Lesson Tests</h2>
        <button onClick={handleCreateTest} className="create-btn">
          + Create Lesson Test
        </button>
      </div>

      <div className="info-box">
        <h3>About Lesson Tests</h3>
        <ul>
          <li>Create diagnostic tests targeted to specific lessons</li>
          <li>Build lesson-specific questions directly within each test</li>
          <li>Set maximum number of questions to display per test</li>
          <li><strong>Random Selection:</strong> When a student takes the test, questions are randomly selected from your pool (e.g., create 15 questions, display 10 randomly each time)</li>
          <li>Mix question types: vocabulary, grammar, and comprehension</li>
          <li>Questions are stored within each lesson test (not in global question bank)</li>
        </ul>
      </div>

      {/* Search bar */}
      <div style={{marginBottom: '1.5rem'}}>
        <input
          type="text"
          placeholder="🔍 Search lesson tests by title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '2px solid #ddd',
            borderRadius: '4px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = '#2196F3'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
        {searchQuery && (
          <p style={{marginTop: '0.5rem', color: '#666', fontSize: '0.9rem'}}>
            Showing results for "{searchQuery}"
          </p>
        )}
      </div>

      {lessonTests.length === 0 ? (
        <div className="empty-state">
          <p>No lesson tests created yet.</p>
          <p>Click "Create Lesson Test" to get started.</p>
        </div>
      ) : (
        <div className="tests-grid">
          {Object.entries(testsByLevel).map(([levelId, tests]) => {
            const filteredTests = filterTests(tests);
            
            // Skip levels with no matching tests
            if (filteredTests.length === 0) return null;
            
            return (
              <div key={levelId} className="level-section">
                <h3 className="level-title">{levelNames[levelId]}</h3>
                <div className="lesson-tests-list">
                  {filteredTests.map(test => {
                  const testTypeCounts = { vocab: 0, grammar: 0, comprehension: 0 };
                  (test.questions || []).forEach(q => {
                    if (testTypeCounts.hasOwnProperty(q.testType)) {
                      testTypeCounts[q.testType]++;
                    }
                  });

                  return (
                    <div key={test.id} className="lesson-test-card">
                      <div className="test-card-header">
                        <div>
                          <h4>{test.lessonTitle}</h4>
                          {test.lessonUrl && (
                            <p className="test-meta" style={{marginBottom: '0.25rem'}}>
                              🔗 <a href={test.lessonUrl} target="_blank" rel="noopener noreferrer" style={{color: '#2196F3', textDecoration: 'none'}}>
                                View Lesson
                              </a>
                            </p>
                          )}
                          <p className="test-meta">
                            Created: {new Date(test.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="test-stats">
                          <span className="question-count">
                            {test.questions?.length || 0} questions in pool
                          </span>
                          <span className="display-count">
                            Displays: {Math.min(test.maxQuestions, test.questions?.length || 0)} random
                          </span>
                        </div>
                      </div>

                      <div className="test-card-body">
                        {test.questions?.length === 0 && (
                          <div className="warning">
                            ⚠️ No questions added yet
                          </div>
                        )}
                        {test.questions?.length > 0 && (
                          <>
                            <div className="test-info" style={{marginBottom: '0.5rem'}}>
                              📊 Mix: {testTypeCounts.vocab} vocab · {testTypeCounts.grammar} grammar · {testTypeCounts.comprehension} comprehension
                            </div>
                            {test.questions.length < test.maxQuestions && (
                              <div className="info-message">
                                ℹ️ Pool has {test.questions.length} questions. Add {test.maxQuestions - test.questions.length} more to reach the display limit of {test.maxQuestions}.
                              </div>
                            )}
                            {test.questions.length >= test.maxQuestions && (
                              <div className="info-message" style={{background: '#e8f5e9', borderColor: '#4CAF50', color: '#2e7d32'}}>
                                ✓ Pool is full! Test will randomly select {test.maxQuestions} from {test.questions.length} questions.
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="test-card-actions">
                        <button 
                          onClick={() => handleEditTest(test)} 
                          className="edit-btn"
                        >
                          Manage Questions
                        </button>
                        <button 
                          onClick={() => handleDeleteTest(test.id)} 
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showBuilder && (
        <LessonTestBuilder onClose={handleBuilderClose} />
      )}

      {editingTest && (
        <LessonTestEditor
          test={editingTest}
          onClose={handleEditorClose}
        />
      )}
    </div>
  );
};

export default LessonTestManager;